import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { useCurrency } from '@/lib/platform/contexts/currency-context';
import { getCurrencySymbol } from '@/lib/platform/utils/currency';
import { NumberInput } from '@/components/platform/forms';
import Tooltip from '@/components/platform/ui/Tooltip';
import { tooltipData } from '@/lib/platform/data/tooltip-data';
import type { NumberFormatType } from '@/lib/platform/utils/locale-formatting';
import { AddEntryButton } from './AddEntryButton';
import { useBlankStart } from './use-blank-start';

interface WebTVSectionProps {
  webtvViews: number;
  webtvCpm: number;
  onWebtvViewsChange: (value: number) => void;
  onWebtvCpmChange: (value: number) => void;
  canEdit: boolean;
  numberFormat?: NumberFormatType;
}

export function WebTVSection({
  webtvViews,
  webtvCpm,
  onWebtvViewsChange,
  onWebtvCpmChange,
  canEdit,
  numberFormat = 'eu',
}: WebTVSectionProps) {
  const { darkMode } = useDarkMode();
  const { baseCurrency } = useCurrency();
  const [expanded, expand] = useBlankStart(webtvViews !== 0 || webtvCpm !== 0);

  return (
    <div
      className={`grid grid-cols-1 gap-x-8 gap-y-10 border-b pb-12 md:grid-cols-3 ${darkMode ? 'border-white/10' : 'border-gray-900/10'} ${!canEdit ? 'opacity-70' : ''}`}
    >
      <div>
        <h2 className={`text-base/7 font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
          Web TV / Streaming
          <span
            className={`ml-2 rounded px-2 py-1 text-xs font-medium ${
              darkMode ? 'bg-white/10 text-gray-300' : 'bg-black/6 text-gray-600'
            }`}
          >
            HQ
          </span>
        </h2>
        <p className={`mt-1 text-sm/6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Web TV and streaming platform views and CPM rates.
        </p>
        {!canEdit && (
          <span className="mt-2 inline-block rounded bg-red-500/10 px-2 py-1 text-xs font-medium text-red-500">
            Read Only
          </span>
        )}
      </div>

      {!expanded && (
        <div className="max-w-2xl md:col-span-2">
          <AddEntryButton label="Web TV / Streaming" onClick={expand} disabled={!canEdit} />
        </div>
      )}

      {expanded && (
        <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
          {/* Web TV Views */}
          <div className="sm:col-span-3">
            <NumberInput
              label={
                <>
                  Web TV Views
                  <Tooltip {...tooltipData.webtvViews} wide={true} />
                </>
              }
              value={webtvViews}
              onChange={(value) => onWebtvViewsChange(typeof value === 'number' ? value : 0)}
              decimals={0}
              numberFormat={numberFormat}
              placeholder="0"
              disabled={!canEdit}
            />
          </div>

          {/* Web TV CPM */}
          <div className="sm:col-span-3">
            <NumberInput
              label={
                <>
                  Web TV CPM
                  <Tooltip {...tooltipData.webtvCpm} wide={true} />
                </>
              }
              value={webtvCpm}
              onChange={(value) => onWebtvCpmChange(typeof value === 'number' ? value : 0)}
              decimals={2}
              numberFormat={numberFormat}
              suffix={getCurrencySymbol(baseCurrency)}
              placeholder="0"
              disabled={!canEdit}
            />
          </div>
        </div>
      )}
    </div>
  );
}
